import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from '../../axios-config';
import { UserContext } from '../../contexts/UserContext';
import { OrganizationJobsContext } from '../../contexts/OrganizationJobsContext';
import { useOrganizations } from '../../contexts/OrganizationsContext';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { daysOfWeek } from '../../types/constants';
import { JobSchedule } from '../../types/job';

type Assignment = {
  organizationJobAssignmentId?: number;
  organizationJobId: number;
  userId: string;
  name: string;
  studentName: string;
  jobStartDate: string;
  jobEndDate: string;
  jobPoints: number;
  createdAt: any;
  createdBy: string;
  studentAccount: any;
  organizationJob: any;
  organizationJobAssignmentSchedules?: JobSchedule[];
};

interface TeacherJobAssignmentProps {
  assignmentData?: any;
  onBack: () => void;
  onSuccess?: () => void;
  onAssignmentUpdate?: (updatedItem: any) => void;
}

/**
 * TeacherJobAssignment wraps the AssignmentForm, and also
 * sets up the 'editItem' state from the incoming assignmentData prop.
 */
const TeacherJobAssignment: React.FC<TeacherJobAssignmentProps> = ({
  assignmentData,
  onBack,
  onSuccess,
  //onAssignmentUpdate
}) => {
  const { userProfile } = useContext(UserContext);

  const memoizedAssignmentData = useMemo(() => assignmentData, [assignmentData]);
  const [editItem, setEditItem] = useState<Assignment | null>(null);

  // 1. Setup the editItem from the incoming assignmentData whenever that changes
  //    but do NOT re-run if "editItem" changes, or that can cause a loop.
  useEffect(() => {
    if (!memoizedAssignmentData) return;

    // If your StudentJob object does NOT have a nested “jobAssignment” array,
    // remove that logic and just map its fields directly:
    const linkAssignment = memoizedAssignmentData;

    setEditItem({
      // The user said “memoizedAssignmentData.id is organizationJobAssignmentId”
      organizationJobAssignmentId: linkAssignment.id,
      // The user said “linkAssignment.jobId is the job’s ID”
      organizationJobId: linkAssignment.jobId,
      // If the assignment form expects userId, but you only have studentId:
      userId: linkAssignment.studentId,
      name: linkAssignment.studentName || '',
      studentName: linkAssignment.studentName || '',
      // Convert startDate to YYYY-MM-DD if needed:
      jobStartDate: linkAssignment.startDate ? linkAssignment.startDate.split('T')[0] : '',
      jobEndDate: linkAssignment.jobEndDate
        ? linkAssignment.jobEndDate.split('T')[0]
        : '',
      // If the job’s points are not stored here, default to 0 or null
      jobPoints: linkAssignment.jobPoints || null,
      createdAt: new Date().toISOString(),
      createdBy: userProfile?.userId || '',
      studentAccount: {}, // or whatever you have
      organizationJob: {}, // or you can fetch the actual job object
      // NEW: Populate existing schedules if present
      organizationJobAssignmentSchedules: linkAssignment.organizationJobAssignmentSchedules || []
    });
  }, [memoizedAssignmentData, userProfile]);

  // The form that handles creating/updating an assignment.
  const AssignmentForm = ({
    editItem,
    onClose,
    onSuccess,
    //onAssignmentUpdate
  }: {
    editItem: Assignment | null;
    onClose: () => void;
    onSuccess?: () => void;
    //onAssignmentUpdate?: (updatedItem: any) => void;
  }) => {
    const { students } = useOrganizations();

    // if we have an editItem, use it, else use the raw memo assignment
    const initialAssignment = editItem || memoizedAssignmentData;

    const { organizationJobs } = useContext(OrganizationJobsContext);
    const [schedules, setSchedules] = useState<JobSchedule[]>([]);

    // 2. Basic formState from the initial assignment
    const initialJobId =
      initialAssignment?.organizationJobId ||
      (organizationJobs.length > 0 ? organizationJobs[0].organizationJobId : 0);

    const [formState, setFormState] = useState<Assignment>({
      organizationJobId: initialJobId,
      organizationJobAssignmentId:
        initialAssignment?.id || 0,
      userId: initialAssignment?.userId || '',
      name: initialAssignment?.studentName || '',
      studentName: initialAssignment?.studentName || '',
      jobStartDate: initialAssignment?.jobStartDate
        ? initialAssignment.jobStartDate.split('T')[0]
        : '',
      jobEndDate: initialAssignment?.jobEndDate || '',
      jobPoints: initialAssignment?.jobPoints ?? null,
      studentAccount: initialAssignment?.studentAccount || {},
      createdAt: initialAssignment?.createdAt || new Date().toISOString(),
      createdBy: initialAssignment?.createdBy || '',
      organizationJob:
        organizationJobs.find(
          (j: any) => j.organizationJobId === initialJobId
        ) || {}
    });

    // 3. Points override or not
    const [useDefaultPoints, setUseDefaultPoints] = useState(
      initialAssignment?.jobPoints == null
    );

    // 4. EndDate radio toggle
    const [useEndDate, setUseEndDate] = useState(
      !!initialAssignment?.jobEndDate
    );

    // 5. Which job from the dropdown?
    const selectedJob = useMemo(() => {
      return organizationJobs.find(
        (job: any) => job.organizationJobId === formState.organizationJobId
      );
    }, [organizationJobs, formState.organizationJobId]);

    // If we are *creating* an assignment, we show a student dropdown
    const isEditing = !!editItem;
    const availableStudents = !isEditing
      ? students.slice().sort((a, b) =>
          a.acornUser.fullName.localeCompare(b.acornUser.fullName)
        )
      : [];

    // 6. SCHEDULE OVERRIDE
    // Modified: if there are existing assignment schedules, enable override by default.
    const [useDefaultSchedule, setUseDefaultSchedule] = useState(
      !(initialAssignment?.organizationJobAssignmentSchedules?.length > 0)
    );

    const newEmptySchedule = (): JobSchedule => ({
      dayOfWeek: 0,
      startTime: '09:00',
      endTime: '10:00'
    });

    const handleUseDefaultToggle = (isDefault: boolean) => {
      setUseDefaultSchedule(isDefault);
      if (isDefault) {
        // Clear schedules if user wants to revert to "Use job's default"
        setSchedules([]);
      }
    };

    const handleAddSchedule = () => {
      setSchedules((prev) => [...prev, newEmptySchedule()]);
    };

    const handleRemoveSchedule = (index: number) => {
      setSchedules((prev) => {
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      });
    };

    const handleScheduleChange = (
      index: number,
      field: keyof JobSchedule,
      value: any
    ) => {
      setSchedules((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    };

    // 7. Only re-initialize schedules if user toggles from default -> override,
    //    or if the selected job changes. DO NOT re-run on schedules or it will loop.
    useEffect(() => {
      if (useDefaultSchedule) {
        // user wants job's default => clear out local
        setSchedules([]);
        return;
      }

      // user toggled override => attempt to copy from assignment or job
      if (initialAssignment?.organizationJobAssignmentSchedules?.length) {
        const newSchedules = initialAssignment.organizationJobAssignmentSchedules.map(
          (js: any) => ({
            ...js,
            startTime: js.startTime.slice(0, 5),
            endTime: js.endTime.slice(0, 5)
          })
        );
        setSchedules(newSchedules);
      } else if (selectedJob?.organizationJobSchedules?.length) { // Changed: use job's schedules
        const newSchedules = selectedJob.organizationJobSchedules.map(
          (js: any) => ({
            ...js,
            startTime: js.startTime.slice(0, 5),
            endTime: js.endTime.slice(0, 5)
          })
        );
        setSchedules(newSchedules);
      } else {
        // no schedules found => one blank row
        setSchedules([newEmptySchedule()]);
      }
      // We only want to run this if "useDefaultSchedule" changes or if "selectedJob" changes
    }, [useDefaultSchedule, selectedJob?.organizationJobId]);

    // 8. SUBMIT
    const { setOrganizationAssignments, fetchOrganizationJobAssignments } =
      useContext(OrganizationJobsContext);
    const { userProfile } = useContext(UserContext);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      // If creating a new assignment but no user was chosen, bail
      const userIdValue = formState.userId?.trim() ? formState.userId : null;
      if (!userIdValue && !isEditing) {
        alert('Select a valid student.');
        return;
      }

      const updatedPoints = useDefaultPoints ? null : formState.jobPoints;
      // Format schedules ensuring times are in HH:mm:ss format
      const formattedSchedules =
        !useDefaultSchedule && schedules.length > 0
          ? schedules.map((sch) => ({
              ...sch,
              startTime: sch.startTime.length === 5 ? `${sch.startTime}:00` : sch.startTime,
              endTime: sch.endTime.length === 5 ? `${sch.endTime}:00` : sch.endTime
            }))
          : null;

      const payload = {
        organizationJobAssignmentId: initialAssignment?.organizationJobAssignmentId || 0,
        organizationJobId: formState.organizationJobId,
        userId: isEditing ? initialAssignment?.studentId : userIdValue,
        jobStartDate: new Date(formState.jobStartDate).toISOString(),
        jobEndDate: useEndDate
          ? new Date(formState.jobEndDate).toISOString()
          : null,
        jobPoints: updatedPoints,
        createdAt: isEditing
          ? initialAssignment?.createdAt
          : new Date().toISOString(),
        createdBy: initialAssignment?.createdBy || userProfile?.userId,
        organizationJobAssignmentSchedules: formattedSchedules
      };

      try {
        let response;
        if (isEditing && initialAssignment?.organizationJobAssignmentId) {
          response = await axios.put(
            `/api/OrganizationJobAssignments/${initialAssignment.organizationJobAssignmentId}`,
            payload
          );
          toast.success('Job assignment updated successfully');
          const updatedAssignment = response.data;
          setOrganizationAssignments((prev: any[]) =>
            prev.map((item) =>
              item.organizationJobAssignmentId === updatedAssignment.organizationJobAssignmentId
                ? updatedAssignment
                : item
            )
          );
        } else {
          response = await axios.post('/api/OrganizationJobAssignments', payload);
          toast.success('Job assignment created successfully');
          const newAssignment = response.data;
          setOrganizationAssignments((prev: any) => [...prev, newAssignment]);
        }
        await fetchOrganizationJobAssignments();
        onSuccess?.();
        onClose();
      } catch (err) {
        console.error(err);
        toast.error('Error saving the job assignment');
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Job dropdown */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Job:
          </label>
          <select
            value={formState.organizationJobId}
            onChange={(e) =>
              setFormState({
                ...formState,
                organizationJobId: Number(e.target.value)
              })
            }
            className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg"
          >
            {organizationJobs.map((job: any) => (
              <option key={job.organizationJobId} value={job.organizationJobId}>
                {job.jobName}
              </option>
            ))}
          </select>
        </div>

        {/* Student dropdown or read-only, depending on isEditing */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Student:
          </label>
          {isEditing ? (
            <div className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg">
              {formState.studentName}
            </div>
          ) : (
            <select
              value={formState.userId}
              onChange={(e) => {
                const chosen = availableStudents.find(
                  (s) => s.acornUser.id === e.target.value
                );
                if (chosen) {
                  setFormState((prev) => ({
                    ...prev,
                    userId: chosen.acornUser.id,
                    studentName: chosen.acornUser.fullName
                  }));
                }
              }}
              className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg"
            >
              <option value="">Select student</option>
              {availableStudents.map((stu, index) => (
                <option key={`${stu.acornUser.id}-${index}`} value={stu.acornUser.id}>
                  {stu.acornUser.fullName} ({stu.acornUser.school})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Start Date */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Date:
          </label>
          <input
            type="date"
            value={formState.jobStartDate}
            onChange={(e) =>
              setFormState({ ...formState, jobStartDate: e.target.value })
            }
            className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg"
          />
        </div>

        {/* Radio for end date or indefinite */}
        <div className="mb-4">
          <span className="block text-sm font-medium text-gray-700 mb-1">
            Assignment Status:
          </span>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                checked={!useEndDate}
                onChange={() => {
                  setUseEndDate(false);
                  setFormState((prev) => ({ ...prev, jobEndDate: '' }));
                }}
                className="mr-1"
              />
              <span className="text-sm">Currently Assigned</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={useEndDate}
                onChange={() => setUseEndDate(true)}
                className="mr-1"
              />
              <span className="text-sm">Specify End Date</span>
            </label>
          </div>
        </div>
        {useEndDate && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date:
            </label>
            <input
              type="date"
              value={formState.jobEndDate}
              onChange={(e) =>
                setFormState({ ...formState, jobEndDate: e.target.value })
              }
              className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg"
            />
          </div>
        )}

        {/* Points override toggle */}
        <div className="mb-4">
          <span className="block text-sm font-medium text-gray-700 mb-1">
            Job Points:
          </span>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                checked={useDefaultPoints}
                onChange={() => setUseDefaultPoints(true)}
                className="mr-1"
              />
              <span className="text-sm">
                Use Job Default ({selectedJob?.jobPoints ?? 'N/A'})
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={!useDefaultPoints}
                onChange={() => {
                  setUseDefaultPoints(false);
                  if (!isEditing || !formState.jobPoints) {
                    setFormState(prev => ({
                      ...prev,
                      jobPoints: selectedJob?.jobPoints ?? prev.jobPoints
                    }));
                  }
                }}
                className="mr-1"
              />
              <span className="text-sm">Override</span>
            </label>
          </div>
        </div>
        {!useDefaultPoints && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Override Points
            </label>
            <input
              type="number"
              value={formState.jobPoints ?? ''}
              onChange={(e) =>
                setFormState({ ...formState, jobPoints: +e.target.value })
              }
              className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg"
            />
          </div>
        )}

        {/* Schedule override toggle */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Assignment Schedule
          </label>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="scheduleOption"
                checked={useDefaultSchedule}
                onChange={() => handleUseDefaultToggle(true)}
                className="mr-1"
              />
              <span className="text-sm">Use Job's Default</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="scheduleOption"
                checked={!useDefaultSchedule}
                onChange={() => handleUseDefaultToggle(false)}
                className="mr-1"
              />
              <span className="text-sm">Override</span>
            </label>
          </div>
        </div>

        {/* Show schedule form if override is chosen */}
        {!useDefaultSchedule && (
          <div>
            <h3 className="font-medium text-gray-700 text-lg mb-3">Job Schedules</h3>
            <div className="bg-gray-50/70 p-4 rounded-lg">
              {schedules.map((schedule, index) => (
                <div
                  key={index}
                  className="mb-4 pb-4 border-b last:border-b-0"
                >
                  <div className="flex items-center gap-4 mb-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Day of Week
                      </label>
                      <select
                        value={schedule.dayOfWeek}
                        onChange={(e) =>
                          handleScheduleChange(index, 'dayOfWeek', +e.target.value)
                        }
                        className="px-3 py-2 bg-white border border-gray-200 rounded-lg w-full md:w-40"
                      >
                        {daysOfWeek.map((dayName, i) => (
                          <option key={i} value={i}>
                            {dayName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={schedule.startTime}
                        onChange={(e) =>
                          handleScheduleChange(index, 'startTime', e.target.value)
                        }
                        className="px-3 py-2 bg-white border border-gray-200 rounded-lg w-full md:w-32"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Time
                      </label>
                      <input
                        type="time"
                        value={schedule.endTime}
                        onChange={(e) =>
                          handleScheduleChange(index, 'endTime', e.target.value)
                        }
                        className="px-3 py-2 bg-white border border-gray-200 rounded-lg w-full md:w-32"
                      />
                    </div>

                    {schedules.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveSchedule(index)}
                        className="bg-red-50 text-red-500 p-2 rounded-lg hover:bg-red-100"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={handleAddSchedule}
                className="mt-3 flex items-center justify-center gap-1 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 w-full"
              >
                <Plus className="w-4 h-4" />
                Add Another Schedule
              </button>
            </div>
          </div>
        )}

        {/* submit/cancel */}
        <div className="flex justify-end gap-2">
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
          >
            {isEditing ? 'Update' : 'Create'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
        </div>
      </form>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={onBack}
          className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h2 className="text-xl font-semibold text-gray-800">
          {editItem ? 'Edit Assignment' : 'Create Assignment'}
        </h2>
      </div>

      <div className="dashboard-card p-6">
        <AssignmentForm
          editItem={editItem}
          onClose={onBack}
          onSuccess={onSuccess}
          //onAssignmentUpdate={onAssignmentUpdate}
        />
      </div>
    </div>
  );
};

export default TeacherJobAssignment;
